import React, { Component } from 'react';
import { settings } from 'settings';
import { Loader } from 'components/Loader/Loader';
import Fetch from 'utils/fetch';
import { ReqEnum } from 'const/requestStatus';
import { OrderTable } from './OrderTable';
import Dropdown from 'components/dropdown/Dropdown';
import Button, { ButtonGroup } from 'components/buttons/Button';
import Flatpickr from 'react-flatpickr';
import './PrintList.scss';
import {
  InboundListOrder,
  Region,
  RegionCode,
} from '../../../types/orderTypes';
import dayjs, { Dayjs } from 'dayjs';
import { PlannerMenu } from '../PlannerMenu';
import { downloadBlob, fetchBlob } from '../../../utils/fetchBlob';
import { datePickerInputStyle } from '../../routes/Routes';
import { capitalize } from '../../../utils/strings';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from 'react-router-dom';

interface SenderAndRegion {
  senderName: string;
  inboundRegion: Region;
}

interface State {
  viewTablesByCompany: boolean;
  selectedCompanyName: string;
  showHub: boolean;
  inboundDate: Dayjs;
  ordersAsync: {
    status: ReqEnum;
    data: InboundListOrder[];
  };
  filteredOrders: InboundListOrder[];
  pdfStatus: ReqEnum;
}

export default class PrintList extends Component<RouteComponentProps, State> {
  constructor(props: null) {
    super(props);

    this.state = {
      viewTablesByCompany: false,
      selectedCompanyName: '',
      showHub: false,
      inboundDate: dayjs(),
      ordersAsync: {
        status: ReqEnum.INIT,
        data: [],
      },
      filteredOrders: [],
      pdfStatus: ReqEnum.INIT,
    };
  }

  componentDidMount(): void {
    this.fetchAllUndeliveredOrdersToday();
  }

  filterByCompany = (companyName: string): void => {
    const orders = this.state.ordersAsync.data;

    const filteredOrders = orders.filter(
      order => order.senderName === companyName
    );

    this.setState({
      filteredOrders,
      selectedCompanyName: companyName,
      viewTablesByCompany: companyName !== '',
    });
  };

  getFilteredListByCompany = (
    companyAndRegion: SenderAndRegion,
    orders: InboundListOrder[]
  ): InboundListOrder[] => {
    return orders.filter(
      order =>
        order.senderName === companyAndRegion.senderName &&
        order.inboundRegion === companyAndRegion.inboundRegion
    );
  };

  filterData = async (allUndeliveredOrders: InboundListOrder[]) => {
    const ordersTodayBasedOnHub = this.state.showHub
      ? allUndeliveredOrders.filter(item => !item.isHubOrder)
      : allUndeliveredOrders;

    return ordersTodayBasedOnHub;
  };

  fetchAllUndeliveredOrdersToday = async (): Promise<void> => {
    this.setState({ ordersAsync: { status: ReqEnum.INIT, data: [] } });

    const date = this.state.inboundDate.format('YYYY-MM-DD');

    const response = await Fetch.get({
      url: `${settings.newApiUrl}/order/inbound?date=${date}`,
    });

    if (response.status !== 200) {
      this.setState({
        ordersAsync: {
          status: ReqEnum.ERROR,
          data: [],
        },
      });
      return;
    }
    const allUndeliveredOrders = (await response.json()) || [];

    this.setState({
      ordersAsync: {
        status: ReqEnum.SUCCESS,
        data: await this.filterData(allUndeliveredOrders),
      },
    });
  };

  findUniqueSenderNameList = (
    orders: InboundListOrder[]
  ): SenderAndRegion[] => {
    const uniqueSendersAndRegions: SenderAndRegion[] = [];
    orders
      .filter(order => order.senderName != null)
      .forEach(order => {
        const senderAndRegion: SenderAndRegion = {
          senderName: order.senderName,
          inboundRegion: order.inboundRegion,
        };
        if (
          !uniqueSendersAndRegions.some(
            item =>
              item.inboundRegion === senderAndRegion.inboundRegion &&
              item.senderName === senderAndRegion.senderName
          )
        ) {
          uniqueSendersAndRegions.push(senderAndRegion);
        }
      });
    return uniqueSendersAndRegions.sort((a, b) =>
      a.senderName.localeCompare(b.senderName)
    );
  };

  viewTablesByCompany = (): void => {
    this.filterByCompany('');
    this.setState({ viewTablesByCompany: !this.state.viewTablesByCompany });
  };

  showOrHideHubOrders = (): void => {
    const showHub = this.state.showHub;
    this.setState({
      showHub: !showHub,
    });
    this.fetchAllUndeliveredOrdersToday();
  };

  async getOrderPDFs(
    companyAndRegion: SenderAndRegion,
    orderIds: string
  ): Promise<void> {
    const url = `${settings.newApiUrl}/order/labels?orderIds=${orderIds}`;
    const blob = await fetchBlob(url);
    const fileName = `porterbuddy-orders-${companyAndRegion.senderName}-${
      companyAndRegion.inboundRegion
    }-${dayjs().format('YYYY-MM-DD')}.pdf`;

    const openInNewWindow = true;
    const download = true;

    if (blob) {
      downloadBlob(
        blob,
        fileName,
        download,
        openInNewWindow ? '_blank' : '_self'
      );
    }
  }

  getPDF = async (
    companyAndRegion: SenderAndRegion,
    orders: InboundListOrder[]
  ) => {
    this.setState({
      pdfStatus: ReqEnum.PENDING,
    });
    const orderIds = orders.map(order => order.orderId).join(',');
    if (orderIds.length) {
      await this.getOrderPDFs(companyAndRegion, orderIds);
    }
    this.setState({
      pdfStatus: ReqEnum.SUCCESS,
    });
  };

  selectDate = (newInboundDate: Dayjs): void => {
    this.setState({ inboundDate: newInboundDate }, () =>
      this.fetchAllUndeliveredOrdersToday()
    );
  };

  render(): JSX.Element {
    const {
      filteredOrders,
      selectedCompanyName,
      viewTablesByCompany,
      inboundDate,
    } = this.state;

    const status = this.state.ordersAsync.status;

    let mainContent = <Loader type="tile" />;

    if (status === ReqEnum.SUCCESS) {
      let orders = this.state.ordersAsync.data;

      const uniqueSenderNameList = this.findUniqueSenderNameList(orders);

      if (filteredOrders.length !== 0) {
        orders = filteredOrders;
      }

      const today = dayjs();
      mainContent = (
        <div className="orders-today">
          <Helmet>
            <title>Planner Inbound List</title>
          </Helmet>
          <div className="orders-today__logo-for-print">
            <img
              src={`${settings.assetsBaseURL}/images/porterbuddy/logo/porterbuddy_color_logo_black_text.svg`}
              alt="Porterbuddy"
            />
          </div>
          <div className="orders-today__header">
            <h3 className="orders-today__header-screen">
              <span>Leveranseliste: &nbsp;</span>
              <ButtonGroup>
                <Button
                  title="<"
                  onClick={() => this.selectDate(inboundDate.add(-1, 'day'))}
                  disabled={inboundDate.isSame(today, 'day')}
                />
                <Flatpickr
                  onChange={selectedDates =>
                    this.selectDate(dayjs(selectedDates[0]))
                  }
                  value={inboundDate.toDate()}
                  options={{
                    dateFormat: 'D d. M',
                    shorthandCurrentMonth: true,
                    locale: { firstDayOfWeek: 1 },
                  }}
                  render={(props, ref) => (
                    <input
                      style={datePickerInputStyle}
                      className={'form-control'}
                      type="text"
                      ref={ref}
                    />
                  )}
                />
                <Button
                  title=">"
                  onClick={() => this.selectDate(inboundDate.add(+1, 'day'))}
                />
              </ButtonGroup>
            </h3>
            <h3 className="orders-today__header-print">
              {inboundDate.format('DD. MMM YYYY')} - Leveranseliste
            </h3>
            <div className="orders-today__header-hubfilter">
              <input
                type="checkbox"
                onChange={() => this.showOrHideHubOrders()}
                checked={this.state.showHub}
                style={{ marginRight: 15 }}
                id="showHubCheck"
              />
              <label htmlFor="showHubCheck">{'Only Non Hub Orders  '}</label>
              <div style={{ width: 10 }} />
            </div>

            <Button
              title="View all orders"
              className="pb-filter-by-company-button"
              onClick={() => this.viewTablesByCompany()}
            />
            {selectedCompanyName.length > 0 && (
              <div className="selectedCompanyName">
                {' '}
                <h3> {selectedCompanyName} </h3>
              </div>
            )}
            <div className="pb-filter-by-company-button">
              <Dropdown
                placeholder={
                  selectedCompanyName
                    ? selectedCompanyName
                    : 'Filter by company'
                }
                onChange={name => this.filterByCompany(name)}
                names={uniqueSenderNameList.map(
                  item =>
                    `${item.senderName} (${RegionCode[item.inboundRegion]})`
                )}
              />
            </div>
            <Button
              title="Print"
              className="orders-today__print-button"
              onClick={() => window.print()}
            />
          </div>
          {!viewTablesByCompany &&
            uniqueSenderNameList.map(companyAndRegion => (
              <div
                key={`${companyAndRegion.senderName}_${companyAndRegion.inboundRegion}`}
              >
                <div style={{ height: 30 }} />
                <h3>
                  {companyAndRegion.senderName}
                  {' ('}
                  {capitalize(companyAndRegion.inboundRegion)}
                  {')'}
                </h3>
                <OrderTable
                  orders={this.getFilteredListByCompany(
                    companyAndRegion,
                    orders
                  )}
                  region={companyAndRegion.inboundRegion}
                />
                <div style={{ height: 15 }} />
                <Button
                  title={`Print all the labels for ${
                    companyAndRegion.senderName
                  } (${RegionCode[companyAndRegion.inboundRegion]})`}
                  loading={this.state.pdfStatus === ReqEnum.PENDING}
                  onClick={() =>
                    this.getPDF(
                      companyAndRegion,
                      this.getFilteredListByCompany(companyAndRegion, orders)
                    )
                  }
                />
              </div>
            ))}
          {viewTablesByCompany && (
            <OrderTable orders={orders} region={orders[0].inboundRegion} />
          )}
          {orders.length === 0 && <OrderTable orders={[]} />}
          <div className="orders-today__signatures">
            <p style={{ fontSize: '1.8rem', marginBottom: 10 }}>
              Signaturfelt kun til bruk ved henting og redistribusjon via
              Porterbuddy:
            </p>
            <h5>Utlevering fra </h5>
            <p>
              Tid hentet:
              <br />
              Signatur avsender:
              <br />
              Signatur Porterbuddy (bud):
            </p>
            <h5>Innsjekk hos Porterbuddy</h5>
            <p>Tid levert:</p>
          </div>
        </div>
      );
    }

    return (
      <>
        <PlannerMenu />
        {mainContent}
      </>
    );
  }
}
